import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Channel,
  ChannelCreateArgs,
  ChannelDeleteArgs,
  ChannelNotificationPreferenceUpdateArgs,
  ChannelsArgs,
  channelsNewApi,
  ChannelUpdateArgs,
} from '../services';

interface ChannelsState {
  isFetched: boolean;
  channels: Channel[];
}

const initialState: ChannelsState = {
  isFetched: false,
  channels: [],
};

export const getChannels = createAsyncThunk(
  'getChannels',
  async (args: ChannelsArgs, { dispatch }) => {
    return dispatch(channelsNewApi.endpoints.channels.initiate(args)).unwrap();
  }
);

export const channelCreate = createAsyncThunk(
  'channelCreate',
  async (args: ChannelCreateArgs, { dispatch }) => {
    return dispatch(channelsNewApi.endpoints.channelCreate.initiate(args)).unwrap();
  }
);

export const channelUpdate = createAsyncThunk(
  'channelUpdate',
  async (args: ChannelUpdateArgs, { dispatch }) => {
    return dispatch(channelsNewApi.endpoints.channelUpdate.initiate(args)).unwrap();
  }
);

export const channelDelete = createAsyncThunk(
  'channelDelete',
  async (args: ChannelDeleteArgs, { dispatch }) => {
    const deleted = await dispatch(channelsNewApi.endpoints.channelDelete.initiate(args)).unwrap();
    return { ...args, deleted };
  }
);

export const channelNotificationPreferenceUpdate = createAsyncThunk(
  'channelNotificationPreferenceUpdate',
  async (args: ChannelNotificationPreferenceUpdateArgs, { dispatch }) => {
    const updated = await dispatch(
      channelsNewApi.endpoints.channelNotificationPreferenceUpdate.initiate(args)
    ).unwrap();

    return { ...args, updated };
  }
);

const ChannelsSlice = createSlice({
  name: 'channels',
  initialState,
  reducers: {
    updateChannelsPositionOnDrop: (
      state,
      action: PayloadAction<{ draggingChannelId: number; targetChannelId: number }>
    ) => {
      const { draggingChannelId, targetChannelId } = action.payload;

      const draggingChannel = state.channels.find(({ id }) => id === draggingChannelId);
      const targetChannel = state.channels.find(({ id }) => id === targetChannelId);

      if (!draggingChannel || !targetChannel) {
        return;
      }

      const { position: draggingPosition } = draggingChannel;
      const { position: targetPosition } = targetChannel;

      if (draggingPosition < targetPosition) {
        state.channels
          .filter(({ position }) => position > draggingPosition && position < targetPosition)
          .forEach((channel) => (channel.position -= 1));

        draggingChannel.position = targetPosition;

        targetChannel.position -= 1;
      } else {
        state.channels
          .filter(({ position }) => position < draggingPosition && position > targetPosition)
          .forEach((channel) => (channel.position += 1));

        draggingChannel.position = targetPosition;

        targetChannel.position += 1;
      }

      state.channels.sort(({ position: posA }, { position: posB }) => posA - posB);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChannels.fulfilled, (state, action) => {
        state.isFetched = true;
        state.channels = action.payload;
      })
      .addCase(channelNotificationPreferenceUpdate.fulfilled, (state, action) => {
        const { updated, channelId, fields } = action.payload;

        if (!updated) {
          return;
        }

        const channel = state.channels.find(({ id }) => id === channelId);

        if (!channel) {
          return;
        }

        channel.userNotificationPreference.allowToReceiveChannelNotification =
          fields.allowToReceiveChannelNotification;
      })
      .addCase(channelCreate.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!payload) {
          return;
        }

        state.channels.unshift(payload);
      })
      .addCase(channelUpdate.fulfilled, (state, action) => {
        const { payload } = action.payload;

        const channelToUpdateIndex = state.channels.findIndex(({ id }) => id === payload.id);

        if (channelToUpdateIndex === -1) {
          return;
        }

        state.channels[channelToUpdateIndex] = {
          ...state.channels[channelToUpdateIndex],
          ...payload,
        };
      })
      .addCase(channelDelete.fulfilled, (state, action) => {
        const { deleted, channelId } = action.payload;

        if (!deleted) {
          return;
        }

        state.channels = state.channels.filter(({ id }) => id !== channelId);
      });
  },
});

export const { updateChannelsPositionOnDrop } = ChannelsSlice.actions;
export default ChannelsSlice.reducer;
